import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import ElevatedPaper from "@/components/ElevatedPaper";
import Flex from "@/components/Flex";
import PageLayout from "@/components/PageLayout";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { PROVIDER_PATHS } from "@/constants/paths";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useGroupVisitById } from "@/queries/group-visit/use-group-visit-by-id";
import GroupDetails from "./components/GroupDetails";
import {
  GROUP_DETAIL_DEFAULT_TAB,
  GROUP_DETAIL_LABEL,
  GROUP_MESSAGE,
} from "./group-visit-constant";
import { GroupDetailTab } from "./group-visit-types";
import {
  getGroupDetailPath,
  getGroupEditPath,
  isGroupArchived,
  isGroupDetailTab,
  toGroupDetailFromGroupVisit,
} from "./group-visit-utils";

/**
 * Routed group detail screen. The group comes from its own detail API keyed on
 * the route uuid — nothing is carried over from the list — and the active tab
 * is the last path segment, so every tab is directly linkable.
 *
 * @author Rajkumar Rathod
 */
const GroupDetailLayout = () => {
  const { groupVisitId, tab } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const {
    data: savedGroup,
    isLoading,
    isError,
  } = useGroupVisitById(groupVisitId);

  const group = useMemo(
    () => (savedGroup ? toGroupDetailFromGroupVisit(savedGroup) : undefined),
    [savedGroup]
  );

  const goToList = () => navigate(PROVIDER_PATHS.GROUP_VISIT);

  const handleTabChange = (nextTab: GroupDetailTab) => {
    if (groupVisitId) navigate(getGroupDetailPath(groupVisitId, nextTab));
  };

  if (!groupVisitId)
    return <Navigate to={PROVIDER_PATHS.GROUP_VISIT} replace />;

  if (!isGroupDetailTab(tab)) {
    return (
      <Navigate
        to={getGroupDetailPath(groupVisitId, GROUP_DETAIL_DEFAULT_TAB)}
        replace
      />
    );
  }

  return (
    <ElevatedPaper>
      <PageLayout
        header={
          <Flex
            alignItems="center"
            gap={0.5}
            role="button"
            tabIndex={0}
            onClick={goToList}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") goToList();
            }}
            sx={{ cursor: "pointer", width: "fit-content" }}
          >
            <ArrowBackIcon
              sx={{ fontSize: 14, color: CHART_COLORS.btnSecondaryText }}
            />
            <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.btnSecondaryText}>
              {GROUP_DETAIL_LABEL.backToGroups}
            </Text>
          </Flex>
        }
      >
        {isLoading && (
          <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textLabel}>
            {GROUP_MESSAGE.loadingGroup}
          </Text>
        )}

        {!isLoading && (isError || !group) && (
          <Flex flexDirection="column" gap={1} alignItems="flex-start">
            <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textLabel}>
              {GROUP_MESSAGE.loadGroupError}
            </Text>
            <StyledButtonV2
              label={GROUP_DETAIL_LABEL.backToGroups}
              variant="outlined"
              onClick={goToList}
            />
          </Flex>
        )}

        {!isLoading && !isError && group && (
          <Box sx={{ width: "100%" }}>
            <GroupDetails
              group={group}
              activeTab={tab}
              onTabChange={handleTabChange}
              canEdit={!isGroupArchived(group.status)}
              onEditGroupDetails={() =>
                navigate(getGroupEditPath(groupVisitId))
              }
              onPatientRowAction={() =>
                showSnackbar({
                  message: GROUP_MESSAGE.patientUpdated,
                  type: "success",
                })
              }
            />
          </Box>
        )}
      </PageLayout>
    </ElevatedPaper>
  );
};

export default GroupDetailLayout;
